import React, {useMemo, useState} from "react";
import {
    Breadcrumb,
    Layout,
    Typography,
    Divider,
    Input,
    Button,
    Space,
    Card,
    Tag,
    Alert,
    Spin,
    Modal,
    Select,
    message,
} from "antd";
import {
    RobotOutlined,
    SaveOutlined,
    ReloadOutlined,
    EyeOutlined,
    CheckCircleOutlined,
    PictureOutlined,
    TagsOutlined,
    AppstoreOutlined,
} from "@ant-design/icons";
import Api from "../api";

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;
const { TextArea } = Input;

const STEPS = {
    IDLE:       "idle",
    GENERATING: "generating",
    REVIEW:     "review",
    SAVING:     "saving",
    SAVED:      "saved",
};

const FEATURED_IMAGES = [
    { attachment_id: 100172, url: "https://fitlap.ee/wp-content/uploads/2026/06/banner1.jpg" },
    { attachment_id: 100173, url: "https://fitlap.ee/wp-content/uploads/2026/06/banner2.jpg" },
    { attachment_id: 100174, url: "https://fitlap.ee/wp-content/uploads/2026/06/banner3.jpg" },
    { attachment_id: 100175, url: "https://fitlap.ee/wp-content/uploads/2026/06/banner4.jpg" },
];

// ── Image Picker ──────────────────────────────────────────────────────────────
const ImagePicker = ({ selected, onChange, disabled }) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {FEATURED_IMAGES.map((img) => {
            const isSelected = selected === img.attachment_id;
            return (
                <div
                    key={img.attachment_id}
                    onClick={() => !disabled && onChange(img.attachment_id)}
                    style={{
                        cursor:     disabled ? "not-allowed" : "pointer",
                        borderRadius: 8,
                        overflow:   "hidden",
                        border:     isSelected ? "3px solid #1677ff" : "3px solid transparent",
                        boxShadow:  isSelected ? "0 0 0 2px #1677ff40" : "0 1px 4px rgba(0,0,0,0.15)",
                        position:   "relative",
                        transition: "border 0.15s, box-shadow 0.15s",
                        opacity:    disabled ? 0.6 : 1,
                    }}
                >
                    <img
                        src={img.url}
                        alt={`Banner ${img.attachment_id}`}
                        style={{ width: "100%", height: 200, objectFit: "cover", display: "block" }}
                    />
                    {isSelected && (
                        <div style={{
                            position: "absolute", top: 6, right: 6,
                            background: "#1677ff", borderRadius: "50%",
                            width: 22, height: 22,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <CheckCircleOutlined style={{ color: "#fff", fontSize: 13 }} />
                        </div>
                    )}
                </div>
            );
        })}
    </div>
);

// ── Taxonomy editor (categories or tags) ─────────────────────────────────────
// Renders as a Select in "tags" mode so admin can add/remove freely.
const TaxonomyEditor = ({
                            value,
                            onChange,
                            disabled,
                            placeholder,
                            endpoint, // "categories" or "tags"
                        }) => {
    const [options, setOptions] = useState([]);

    const mergedOptions = useMemo(() => {
        const current = value.map((v) => ({
            label: v,
            value: v,
        }));

        const existing = options.filter(
            (o) => !value.includes(o.value)
        );

        return [...current, ...existing];
    }, [value, options]);

    const handleSearch = async (search) => {
        if (!search.trim()) return;

        try {
            const res = await fetch(
                `https://wp.fitlap.ee/wp-json/wp/v2/${endpoint}?search=${encodeURIComponent(search)}&per_page=20`
            );

            const data = await res.json();

            setOptions(
                data.map((item) => ({
                    label: item.name,
                    value: item.name,
                }))
            );
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <Select
            mode="tags"
            showSearch
            filterOption={false}
            onSearch={handleSearch}
            options={mergedOptions}
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            tokenSeparators={[","]}
            style={{ width: "100%" }}
        />
    );
};

// ── Main component ────────────────────────────────────────────────────────────
const GenerateBlogPost = () => {
    const [step,            setStep]           = useState(STEPS.IDLE);
    const [prompt,          setPrompt]         = useState("");
    const [article,         setArticle]        = useState(null);
    const [featuredImageId, setFeaturedImageId] = useState(null);
    const [categories,      setCategories]     = useState([]);
    const [tags,            setTags]           = useState([]);
    const [status,          setStatus]         = useState("draft");
    const [savedPostId,     setSavedPostId]    = useState(null);
    const [previewOpen,     setPreviewOpen]    = useState(false);
    const [error,           setError]          = useState(null);

    // ── Generate ──────────────────────────────────────────────────────────────
    const handleGenerate = async () => {
        if (!prompt.trim()) {
            message.warning("Please enter a topic before generating.");
            return;
        }

        setError(null);
        setStep(STEPS.GENERATING);

        try {
            const res = await Api.post("/admin/v2/blog-post/generate", {
                prompt: prompt.trim(),
            });

            if (res.data.success) {
                const art = res.data.article;
                setArticle(art);
                setFeaturedImageId(FEATURED_IMAGES[0].attachment_id);
                setCategories(art.categories ?? []);
                setTags(art.tags ?? []);
                setStep(STEPS.REVIEW);
            } else {
                throw new Error(res.data.message || "Generation failed.");
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Something went wrong.");
            setStep(STEPS.IDLE);
        }
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        setError(null);
        setStep(STEPS.SAVING);

        const selectedImage = FEATURED_IMAGES.find((img) => img.attachment_id === featuredImageId);

        const featuredImageHtml = selectedImage
            ? `<img src="${selectedImage.url}" alt="${article.title}" style="width:100%;height:auto;margin-bottom:24px;border-radius:8px;" />\n`
            : "";

        const disclaimer = `
<div style="margin-top:24px;font-size:12px;font-style:italic;color:#888;">
  Blogi on kirjutatud AI poolt ja Fitlap ei vastuta selle sisu eest.
</div>`;

        try {
            const res = await Api.post("/admin/v2/blog-post/save", {
                title:             article.title,
                content:           featuredImageHtml + article.content + disclaimer,
                seo_title:         article.seo_title,
                meta_description:  article.meta_description,
                focus_keyword:     article.focus_keyword,
                featured_image_id: featuredImageId,
                status,
                categories,
                tags,
            });

            if (res.data.success) {
                setSavedPostId(res.data.post_id);
                setStep(STEPS.SAVED);
                message.success("Post saved in WordPress!");
            } else {
                throw new Error(res.data.message || "Failed to save post.");
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Something went wrong.");
            setStep(STEPS.REVIEW);
        }
    };

    // ── Reset ─────────────────────────────────────────────────────────────────
    const handleReset = () => {
        setStep(STEPS.IDLE);
        setArticle(null);
        setPrompt("");
        setError(null);
        setSavedPostId(null);
        setCategories([]);
        setTags([]);
        setFeaturedImageId(null);
    };

    const updateField = (field, value) =>
        setArticle((prev) => ({ ...prev, [field]: value }));

    const isBusy = step === STEPS.SAVING;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Content style={{ margin: "0 16px" }}>
            <Breadcrumb style={{ margin: "16px 0" }}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item>Blog Generator</Breadcrumb.Item>
            </Breadcrumb>

            <div className="site-layout-card">
                <Divider orientation="left">
                    <Title level={4} style={{ margin: 0 }}>
                        <RobotOutlined style={{ marginRight: 8 }} />
                        AI Blog Post Generator
                    </Title>
                </Divider>

                {error && (
                    <Alert
                        type="error"
                        message={error}
                        closable
                        onClose={() => setError(null)}
                        style={{ marginBottom: 20 }}
                    />
                )}

                {/* ── Step 1: Prompt ── */}
                {(step === STEPS.IDLE || step === STEPS.GENERATING) && (
                    <div style={{ maxWidth: 700 }}>
                        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                            Describe the blog post topic and any specific angles you want covered.
                            The AI will generate a full SEO-optimised article in Estonian — including
                            suggested categories and tags — for you to review.
                        </Paragraph>

                        <TextArea
                            rows={4}
                            placeholder='e.g. "Kõrge valgusisaldusega hommikusöögi eelised kaalulanguse jaoks"'
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            disabled={step === STEPS.GENERATING}
                            style={{ marginBottom: 16 }}
                        />

                        <Button
                            type="primary"
                            icon={<RobotOutlined />}
                            loading={step === STEPS.GENERATING}
                            onClick={handleGenerate}
                            size="large"
                        >
                            {step === STEPS.GENERATING ? "Generating…" : "Generate Blog Post"}
                        </Button>

                        {step === STEPS.GENERATING && (
                            <Paragraph type="secondary" style={{ marginTop: 12 }}>
                                This usually takes 20–40 seconds. Hang tight…
                            </Paragraph>
                        )}
                    </div>
                )}

                {/* ── Step 2: Review ── */}
                {(step === STEPS.REVIEW || step === STEPS.SAVING) && article && (
                    <Spin spinning={isBusy} tip="Saving to WordPress…">
                        <Space direction="vertical" size="large" style={{ width: "100%" }}>

                            {/* Featured Image */}
                            <Card
                                size="small"
                                title={
                                    <Space>
                                        <PictureOutlined />
                                        <Text strong>Featured Image</Text>
                                        {!featuredImageId && <Tag color="warning">Required</Tag>}
                                    </Space>
                                }
                            >
                                <ImagePicker
                                    selected={featuredImageId}
                                    onChange={setFeaturedImageId}
                                    disabled={isBusy}
                                />
                            </Card>

                            {/* Title */}
                            <Card size="small" title={<Text strong>Post Title</Text>}>
                                <Input
                                    value={article.title}
                                    onChange={(e) => updateField("title", e.target.value)}
                                    size="large"
                                />
                            </Card>

                            {/* Categories & Tags */}
                            <Card
                                size="small"
                                title={
                                    <Space>
                                        <TagsOutlined />
                                        <Text strong>Categories &amp; Tags</Text>
                                        <Tag color="blue" style={{ fontWeight: 400 }}>AI suggested — edit freely</Tag>
                                    </Space>
                                }
                            >
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    <div>
                                        <Space style={{ marginBottom: 6 }}>
                                            <AppstoreOutlined />
                                            <Text type="secondary">Categories</Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                (type &amp; press Enter to add a new one)
                                            </Text>
                                        </Space>
                                        <TaxonomyEditor
                                            endpoint="categories"
                                            value={categories}
                                            onChange={setCategories}
                                            disabled={isBusy}
                                            placeholder="e.g. Toitumine, Treening"
                                        />
                                    </div>
                                    <div>
                                        <Space style={{ marginBottom: 6 }}>
                                            <TagsOutlined />
                                            <Text type="secondary">Tags</Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                (type &amp; press Enter to add a new one)
                                            </Text>
                                        </Space>
                                        <TaxonomyEditor
                                            endpoint="tags"
                                            value={tags}
                                            onChange={setTags}
                                            disabled={isBusy}
                                            placeholder="e.g. valk, kaalulangus, hommikusöök"
                                        />
                                    </div>
                                </Space>
                            </Card>

                            {/* Content */}
                            <Card
                                size="small"
                                title={<Text strong>Generated Content</Text>}
                                extra={
                                    <Button icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)} size="small">
                                        Preview HTML
                                    </Button>
                                }
                            >
                                <TextArea
                                    value={article.content}
                                    onChange={(e) => updateField("content", e.target.value)}
                                    rows={12}
                                    style={{ fontFamily: "monospace", fontSize: 12 }}
                                />
                            </Card>

                            {/* SEO */}
                            <Card size="small" title={<Text strong>SEO Details</Text>}>
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    <div>
                                        <Text type="secondary">SEO Title</Text>
                                        <Input
                                            value={article.seo_title}
                                            onChange={(e) => updateField("seo_title", e.target.value)}
                                            maxLength={60}
                                            showCount
                                            style={{ marginTop: 4 }}
                                        />
                                    </div>
                                    <div>
                                        <Text type="secondary">Meta Description</Text>
                                        <TextArea
                                            value={article.meta_description}
                                            onChange={(e) => updateField("meta_description", e.target.value)}
                                            maxLength={155}
                                            showCount
                                            rows={2}
                                            style={{ marginTop: 4 }}
                                        />
                                    </div>
                                    <div>
                                        <Text type="secondary">Focus Keyword</Text>
                                        <Input
                                            value={article.focus_keyword}
                                            onChange={(e) => updateField("focus_keyword", e.target.value)}
                                            style={{ marginTop: 4 }}
                                        />
                                    </div>
                                </Space>
                            </Card>

                            {/* Publish settings */}
                            <Card size="small" title={<Text strong>Publish Settings</Text>}>
                                <Text type="secondary">Post Status</Text>
                                <br />
                                <Select
                                    value={status}
                                    onChange={setStatus}
                                    style={{ width: 200, marginTop: 4 }}
                                    options={[
                                        { label: "Draft",   value: "draft"   },
                                        { label: "Publish", value: "publish" },
                                    ]}
                                />
                            </Card>

                            {/* Actions */}
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    onClick={handleSave}
                                    disabled={isBusy || !featuredImageId}
                                    size="large"
                                >
                                    Save to WordPress
                                </Button>
                                <Button
                                    size="large"
                                    icon={<ReloadOutlined />}
                                    onClick={handleReset}
                                    disabled={isBusy}
                                >
                                    Start Over
                                </Button>
                            </Space>
                        </Space>
                    </Spin>
                )}

                {/* ── Step 3: Saved ── */}
                {step === STEPS.SAVED && (
                    <div style={{ textAlign: "center", padding: "48px 0" }}>
                        <CheckCircleOutlined style={{ fontSize: 56, color: "#52c41a", marginBottom: 16 }} />
                        <Title level={3}>Post Saved!</Title>
                        <Paragraph type="secondary">
                            <strong>{article?.title}</strong> has been saved in WordPress.
                        </Paragraph>

                        {/* Show the assigned categories & tags */}
                        {(categories.length > 0 || tags.length > 0) && (
                            <div style={{ marginBottom: 16 }}>
                                {categories.map((c) => (
                                    <Tag key={c} icon={<AppstoreOutlined />} color="purple" style={{ marginBottom: 4 }}>{c}</Tag>
                                ))}
                                {tags.map((t) => (
                                    <Tag key={t} icon={<TagsOutlined />} color="cyan" style={{ marginBottom: 4 }}>{t}</Tag>
                                ))}
                            </div>
                        )}

                        {savedPostId && (
                            <a
                                target="_blank"
                                rel="noreferrer"
                                href={`https://wp.fitlap.ee/wp-admin/post.php?post=${savedPostId}&action=edit`}
                            >
                                <Tag color="blue" style={{ marginBottom: 24, fontSize: 13 }}>
                                    WordPress Post ID: {savedPostId} — open in WP Admin ↗
                                </Tag>
                            </a>
                        )}
                        <br />
                        <Button type="primary" icon={<RobotOutlined />} onClick={handleReset} size="large">
                            Generate Another Post
                        </Button>
                    </div>
                )}
            </div>

            {/* ── Preview Modal ── */}
            <Modal
                title="Post Preview"
                open={previewOpen}
                onCancel={() => setPreviewOpen(false)}
                footer={[<Button key="close" onClick={() => setPreviewOpen(false)}>Close</Button>]}
                width={768}
                style={{ top: 20 }}
                styles={{ body: { maxHeight: "80vh", overflowY: "auto" } }}
            >
                <Title>{article?.title}</Title>
                <div dangerouslySetInnerHTML={{ __html: article?.content }} />
                <div style={{ marginTop: 24, fontSize: 12, fontStyle: "italic", color: "#888" }}>
                    Blogi on kirjutatud AI poolt ja Fitlap ei vastuta selle sisu eest.
                </div>
            </Modal>
        </Content>
    );
};

export default GenerateBlogPost;